// src/pages/dashboard/Dashboard.js
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import 'jsvectormap/dist/jsvectormap.min.css';
import { Card, CardBody, CardExpandToggler } from './../../components/card/card.jsx';
import ImageCountViewsList from "../../components/lists/imageCountViewsList.jsx";
import ReusableChartCard from "../../components/charts/ReusableChartCard";
import UserRecipesList from "../../components/lists/UserRecipesList";
import TotalRegisteredUsersCard from '../../components/charts/TotalRegisteredUsersCard.jsx';

import CardLoader from '../../components/card/CardLoader.jsx';
import { DeviceRow } from '../hardware/DeviceRow.jsx';
import DashUserRow from '../../components/lists/DashUserRow.jsx';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useThemeColor } from '../../helpers/ThemeColor.js';
import Chart from "react-apexcharts";

// Helper function for chart options
import { getChartOptions } from '../../helpers/chartOptions';
import TopCardWithModal from '../../components/charts/TopCardWithModal'; 

import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';


// Redux actions
import { 
  fetchDashboardStatsQ1, 
  fetchDashboardStatsQ3, 
  fetchDeviceRecipesStats,
  fetchHardwareChartTileInfo,
  fetchDashboardStatsQ2,
  fetchCuratedRecipesV3,
  fetchIngredientsV4,
  // fetchIngredientsV3,
  fetchBrandsV3,
  fetchInfluencerV3,
  fetchCompareUsersDay,
  fetchCompareUsersWeek,
  fetchCompareUsersMonth,
  fetchCompareUsersYear,
  fetchUserRecipes,
  fetchBrandRecipes,
  fetchCuratedRecipes,
  fetchInfluencerRecipes,
  fetchPlaylistMixlists,
  fetchBrandMixlists,
  fetchInfluencerMixlists,
  fetchUserTileInfo,
  // fetchOrders,
  fetchCraftedCocktailsExpand,
  fetchHardwareUsageStats,
  fetchCompareUsers
} from '../../actions/dashboard_actions';

import { fetchPaginatedDevices } from '../../actions/hardware_actions';
import { fetchUserPageDataPaginated } from '../../actions/users_actions';
import { fetchLatestRecipes } from '../../actions/recipes_actions';
import BqDashboardInsights from '../../components/bigquery/BqDashboardInsights.jsx';


const getThemeColor = () =>
  getComputedStyle(document.body).getPropertyValue("--bs-theme").trim() || "#08b0af";


// 1) Define hardware-specific tooltip:
const hardwareTooltip = {
  bar: {
    x: {
      show: true,
      formatter: (val, opts) => 
        [ 'Previous Week', 'Current Week' ][opts.dataPointIndex] || val
    },
    y: {
      formatter: v => `${v}`
    }
  }
};

// 2) Build your full chartOptions from it:
// const hardwareChartOptions = getChartOptions(hardwareTooltip);

// ─── date‐dimension & device‐type options ────────────────────
const hwPeriodOptions = [
  { value: 'day',    label: 'Daily'   },
  { value: 'week',   label: 'Weekly'  },
  { value: 'month',  label: 'Monthly' },
  { value: 'year',   label: 'Yearly'  },
];

const hwDeviceOptions = [
  { value: 'all', label: 'All Devices' },
  { value: 'coaster',   label: 'Coaster' },
  { value: '360', label: 'Barsys 360' },
  { value: 'shakerpro', label: 'Shaker Pro'},
];

function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const themeColor = useThemeColor();

  const themeColor1 = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

    const themeFont = getComputedStyle(document.body)
  .getPropertyValue('--bs-body-font-family')
  .trim();

  //Latest Recipes state from redux
  const {
    latest_recipes_info,
    loading_latest_recipes,
    latest_recipes_error
  } = useSelector(state => state.recipes);


  // console.log('My fonts are:', themeFont)

  //   useEffect(() => {
  //   // This function will be called on 'theme-reload'
  //   const handleThemeReload = () => setThemeColor(getThemeColor());
  //   window.addEventListener('theme-reload', handleThemeReload);
  //   // On mount, update
  //   handleThemeReload();
  //   return () => window.removeEventListener('theme-reload', handleThemeReload);
  // }, []);

  

  //
  // ─── 1) Add local “limit” state for hardware devices ───────────────
  //

  // We’ll mirror the pattern from Users: one state for the immediate input,
  // another for the debounced value we actually send to the API.
  //

  const [hardwareLimitInput, setHardwareLimitInput] = useState("10");
  const [hardwareLimit, setHardwareLimit] = useState("10");

  // On‐change handler for the limit textbox:
  const handleHardwareLimitChange = (e) => {
    setHardwareLimitInput(e.target.value);
  };

// Debounce the user’s typing by 1 second, then update hardwareLimit.
  const hardwareDebounceRef = useRef(null);
  useEffect(() => {
    // Clear any pending timer
    if (hardwareDebounceRef.current) {
      clearTimeout(hardwareDebounceRef.current);
  }

  // Only send API call if there’s something nonempty in the limit field
    const trimmed = hardwareLimitInput.trim();
      if (trimmed) {
        hardwareDebounceRef.current = setTimeout(() => {
          setHardwareLimit(trimmed);
        }, 1000);
      }

    return () => {
      clearTimeout(hardwareDebounceRef.current);
    };
  }, [hardwareLimitInput]);

  //
  // ─── 2) Whenever hardwareLimit changes, fetch page 1 of devices with “perPage = hardwareLimit” ───────────────
  //

  useEffect(() => {
    // Only dispatch if we have a numeric limit
    const n = Number(hardwareLimit);
    if (!isNaN(n) && n > 0) {
      // We always force page = 1 in Dashboard
      dispatch(fetchPaginatedDevices(1, n));
    }
  }, [hardwareLimit, dispatch]);

  //
  // ─── 3) Read Redux state.hardware for the returned devices (and loading flag) ───────────────
  //

  const {
    paginated_devices: dashboardDevices = [],
    paginated_devices_loading: dashboardDevicesLoading,
  } = useSelector((state) => state.hardware);

  //
  // ─── 4) Because we’re always fetching page 1, “startIndex” = 1, and “endIndex” = min(limit, total) ───────────────
  //

  // const totalDevicesFromRedux = useSelector((state) => state.hardware.paginated_devices_total) || 0;
  // const startIndexHardware = totalDevicesFromRedux > 0 ? 1 : 0;
  // const endIndexHardware = Math.min(totalDevicesFromRedux, Number(hardwareLimit) || 0);
// ─── Also: Immediately fire our hardware fetch with the default limit ("10")
//     but only once on mount (or if you really want to re-fetch whenever hardwareLimit changes, include it in the deps array):
// useEffect(() => {
//   dispatch(fetchPaginatedDevices(1, Number(hardwareLimit)));
// }, [dispatch, hardwareLimit]);


  // const greenThemeColor = getComputedStyle(document.body)
  // .getPropertyValue("--bs-success")
  // .trim() || '#28a745';

  // ─── LAST CONNECTED USERS “limit” state ──────────────────────────
  const [userLimitInput, setUserLimitInput] = useState("10");
  const [userLimit, setUserLimit] = useState("10");
  const userDebounceRef = useRef(null);

  // ─── Debounce userLimitInput → userLimit (1 second) ──────────────────────
  useEffect(() => {
    if (userDebounceRef.current) clearTimeout(userDebounceRef.current);
    const trimmed = userLimitInput.trim();
    if (trimmed) {
      userDebounceRef.current = setTimeout(() => {
        setUserLimit(trimmed);
      }, 1000);
    }
    return () => clearTimeout(userDebounceRef.current);
  }, [userLimitInput]);

// ─── Whenever userLimit changes, fetch users page=1, perPage=userLimit ─────
useEffect(() => {
  const n = Number(userLimit);
  if (!isNaN(n) && n > 0) {
    // build minimal “users” parameters (match Users.jsx defaults).
    // End = now: the API filters users.updated BETWEEN start AND end, so a
    // fixed end date silently drops everyone active after it.
    const startTime = "2010-01-01T00:00:00";
    const endTime = new Date().toISOString();
    const filterParam = "0";        // Registered Users
    const datefilterParam = 0;      // “Yearly” (default)
    const countryParam = "0";
    const ageStartParam = "0";
    const ageEndParam = "150";
    dispatch(
      fetchUserPageDataPaginated(
        startTime,
        endTime,
        filterParam,
        datefilterParam,
        countryParam,
        ageStartParam,
        ageEndParam,
        1,               // always page=1
        String(n),       // itemsPerPage = userLimit
        null             // no search term
      )
    );
  }
}, [userLimit, dispatch]);

  // (A second, identical fetch effect was removed: the effect above already runs
  // on mount and whenever userLimit changes.)

  // ─── New selectors for paginated “users” ───────────────────────────────
  const {
    users_paginated_info = {},
    loading_users_paginated: usersLoadingPaginated,
  } = useSelector((state) => state.users);
  const paginatedUserList = users_paginated_info.userdata || [];
  // const totalUsersFromRedux = users_paginated_info.totalRegisterUser || 0;

  // const startIndexUser = totalUsersFromRedux > 0 ? 1 : 0;
  // const endIndexUser = Math.min(totalUsersFromRedux, Number(userLimit) || 0);


  // At the top of Dashboard component:
  const [currentLimit, setCurrentLimit] = useState("10");


  // Select Q1 and Q3 dashboard states
  const { stats_q1, loading_q1, error_q1 } = useSelector((state) => state.dashboard);
  const { stats_q3, loading_q3, error_q3 } = useSelector((state) => state.dashboard);
  const { stats_q2, loading_q2, error_q2 } = useSelector((state) => state.dashboard);
  const { 
          curated_recipes_v3_info, 
          loading_curated_recipes_v3_info, 
          curated_recipes_v3_error 
        } = useSelector((state) => state.dashboard);

    // For ingredients (new data)
    const { 
            ingredients_v3_info, 
            loading_ingredients_v3_info, 
            ingredients_v3_error 
          } = useSelector((state) => state.dashboard);
    
      // For Brands V3 data
      const { 
        brands_v3, 
        loading_brands_v3, 
        brands_v3_error 
      } = useSelector((state) => state.dashboard);   

      // For Influencers V3 data
      const { 
        loading_influencer_v3, 
        influencerV3Data, 
        errorInfluencerV3 
      } = useSelector((state) => state.dashboard);   


      // NEW: Select hardware chart tile info from Redux
      const { 
        hardware_chart_tile_info,
        hardware_chart_tile_info_loading,
        hardware_chart_tile_info_error,
        hardware_usage_expand_info,
        loading_hardware_usage_expand,

      } = useSelector((state) => state.dashboard);


      //Orders Table Data
      // const {
      //   orders_info,
      //   loading_orders_info,
      //   order_details_info,
      //   loading_order_details
      // } = useSelector((state) => state.dashboard);


      // Set initial active tab to "recipes" (correctly spelled)
      // For the first list of Top Recipes (or whatever its name is)
      const [topRecipesActiveTab, setTopRecipesActiveTab] = useState("recipes");
      const [topRecipesLimit, setTopRecipesLimit] = useState("10");

      // Define a ref to store the previous active tab value.
      const prevTopRecipesActiveTab = useRef(topRecipesActiveTab);

      // Custom tabs array with correct spellings
      const producttabs = [
        { id: "recipes", label: "Recipes", active: true },
        { id: "ingredients", label: "Ingredients", active: false },
        { id: "brands", label: "Brands", active: false },
        { id: "influencers", label: "Influencers", active: false },
      ];

  // Local state for static asset data
  const [statsData, setStatsData] = useState();
  const [serverData, setServerData] = useState();
  const [countryData, setCountryData] = useState();
  const [sourceData, setSourceData] = useState();
  const [sourceChartData, setSourceChartData] = useState();
  const [productData, setProductData] = useState();
  const [activityLogData, setActivityLogData] = useState();
  

  // Chart options state; initialize with default options.
  const [chartOptions, setChartOptions] = useState(getChartOptions());

  const currentYear = new Date().getFullYear();
  const startTime = new Date(currentYear-20, 0, 1).toISOString(); // January 1, currentYear-1 at 00:00:00 
  const endTime = new Date(currentYear, 11, 31, 23, 59, 59).toISOString(); // December 31, currentYear at 23:59:59

    // Calculate the start and end dates for the current month's orders:
    const currentDate = new Date();
  
    // First day of the current month at 00:00:00
    const ordersDateFrom = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    ).toISOString();
  
    // Last day of the current month at 23:59:59
    const ordersDateTo = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0,
      23,
      59,
      59
    ).toISOString();

    const defaultNewVsActiveStat = {
      title: "NEW USERS VS ACTIVE USERS",
      total: "...",
      secondaryValue: "...",
      chartType: "line",
      chartHeight: 300, // Or whatever your default chart height is
      info: [
        { icon: "fa fa-user-plus", text: "New Users: ...", tooltip: "New Users" },
        { icon: "fa fa-user", text: "Active Users: ...", tooltip: "Active Users" },
      ]
    };



  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchDashboardStatsQ1());
    dispatch(fetchDashboardStatsQ2());    // NEW: Dispatch Q2 action
    dispatch(fetchDashboardStatsQ3());
    // dispatch(fetchDeviceRecipesStats(startTime, endTime, 0, "DESC",));
    dispatch(fetchHardwareChartTileInfo());
    dispatch(fetchCuratedRecipesV3("desc", 0, 0, startTime, endTime, "10"));
    // dispatch(fetchInfluencerV3("desc", 0, 0, startTime, endTime, "10"));
    dispatch(fetchCompareUsersDay("50"));
    //Dispatch User Recipes to fetch user recipes data
    dispatch(fetchUserRecipes("desc", 0, startTime, endTime, "10"));
    dispatch(fetchPlaylistMixlists("desc", 3, startTime, endTime, "10"));
    dispatch(fetchUserTileInfo("0", "barsysdb.users", startTime, endTime, "0", "99"));
    //NEW: Dispatch action to fetch orders
    // dispatch(fetchOrders( ordersDateFrom, ordersDateTo ));

  }, [dispatch]);

// EFFECT 1: Immediate API call when activeTab changes (DO NOT include currentLimit)
useEffect(() => {
  if (topRecipesActiveTab === "ingredients") {
    // dispatch(fetchIngredientsV3("desc", 0, 0, startTime, endTime, topRecipesLimit));
    // dispatch(fetchIngredientsV4("desc", topRecipesLimit));
    dispatch(fetchIngredientsV4({ orderRank: "desc", limit: Number(topRecipesLimit) || 10, source: "mysql" }));
  } else if (topRecipesActiveTab === "recipes") {
    dispatch(fetchCuratedRecipesV3("desc", 0, 0, startTime, endTime, topRecipesLimit));
  } else if (topRecipesActiveTab === "brands") {
    console.log('Calling fetchBrandV3 with:', startTime, endTime, topRecipesLimit);
    dispatch(fetchBrandsV3("desc", 0, 0, startTime, endTime, topRecipesLimit));
  } else if (topRecipesActiveTab === "influencers") {
    dispatch(fetchInfluencerV3("desc", 0, 0, startTime, endTime, topRecipesLimit));
  }
}, [topRecipesActiveTab, startTime, endTime, dispatch]);


// Add these refs at the top of your component (near your state declarations)
// const prevActiveTab = useRef(activeTab);
const isInitialMount = useRef(true);

// EFFECT 2: Debounced API call when currentLimit changes 
useEffect(() => {
  if (isInitialMount.current) {
    isInitialMount.current = false;
    return;
  }
  
  if (topRecipesActiveTab !== prevTopRecipesActiveTab.current) {
    prevTopRecipesActiveTab.current = topRecipesActiveTab;
    return;
  }

  const timer = setTimeout(() => {
    if (topRecipesActiveTab === "ingredients") {
      dispatch(fetchIngredientsV4("desc",topRecipesLimit));
    } else if (topRecipesActiveTab === "recipes") {
      dispatch(fetchCuratedRecipesV3("desc", 0, 0, startTime, endTime, topRecipesLimit));
    } else if (topRecipesActiveTab === "brands") {
      dispatch(fetchBrandsV3("desc", 0, 0, startTime, endTime, topRecipesLimit));
    } else if (topRecipesActiveTab === "influencers") {
      dispatch(fetchInfluencerV3("desc", 0, 0, startTime, endTime, topRecipesLimit));
    }
  }, 1000);

  return () => clearTimeout(timer);
}, [topRecipesLimit, topRecipesActiveTab, dispatch, startTime, endTime]);


  // When tab changes, we want an immediate API call.
  const handleTabChange = (tabId) => {
    setTopRecipesActiveTab(tabId);
    // The above useEffect (Effect 1) will fire on activeTab change.
  };
  
  // When limit input changes, we update our state.
  const handleLimitChange = useCallback((newLimit) => {
    setCurrentLimit(newLimit);
  }, []);


  // NEW: Prepare Q2 stat object using the output from fetchDashboardStatsQ2
  const q2Stat = stats_q2 ? (() => {
    const prev = Number(stats_q2.activeuserpreviousWeekUser) || 0;
    const curr = Number(stats_q2.activeuserlastWeekUser) || 0;
    const total = Number(stats_q2.allActiveUser) || 0;
    // Calculate percentage change (0% if previous week is 0)
    const changePercentage = prev > 0 ? (((curr - prev) * 100) / prev).toFixed(2) + "%" : "0%";
    // Calculate the percentage that current represents of the total
    const relativeToTotal = total > 0 ? ((curr * 100) / total).toFixed(2) + "%" : "0%";
    
    return {
      title: "ACTIVE USERS",
      total: total,
      chartType: "bar",
      chartHeight: 60,
      chartData: [
        {
          name: "Active Users",
          data: [
            { x: "Previous Week", y: prev },
            { x: "Current Week", y: curr }
          ]
        }
      ],
      info: [
        { icon: "fa fa-chart-line fa-fw", text: `${prev} - Previous Week`, tooltip: "Previous Week" },
        { icon: "fa fa-chart-bar fa-fw", text: `${curr} - Current Week`, tooltip: "Current Week" },
        { icon: "fa fa-percent fa-fw", text: `Change: ${changePercentage}`, tooltip: "Percentage Change from Previous Week" },
        { icon: "fa fa-percent fa-fw", text: `Current is ${relativeToTotal} of Total`, tooltip: "Relative to Total Active Users" }
      ]
    };
  })() : null;


  // Define custom tooltip for recipes (Users and Cocktails)
  const customTooltip = {
    bar: {
      x: {
        show: true,
        formatter: (val, opts) => {
          const labels = ['Previous Week', 'Current Week'];
          return typeof opts.dataPointIndex !== 'undefined'
            ? labels[opts.dataPointIndex]
            : val;
        }
      },
      y: {
        formatter: (val) => `${val}`
      }
    }
  };

  // NEW: Define custom tooltip for hardware that appends " devices" to y-values.
  const hardwareCustomTooltip = {
    bar: { // Use 'line' instead of 'bar' for line charts
      x: {
        show: true,
        formatter: (val, opts) => {
          const labels = ['Previous Week', 'Current Week'];
          return typeof opts.dataPointIndex !== 'undefined'
            ? labels[opts.dataPointIndex]
            : val;
        }
      },
      y: {
        formatter: (val) => `${val}`
      }
    }
  };

  // Generate chart options:
  const customChartOptions = getChartOptions(customTooltip);
  // For hardware, force xaxis.categories so that the tooltip displays our labels.
const hardwareChartOptions = {
  ...getChartOptions(hardwareCustomTooltip),
  plotOptions: {
    bar: {
      distributed: true
    }
  }
};  // const hardwareChartOptions = {
  //   ...getChartOptions(hardwareCustomTooltip),
  //   xaxis: {
  //     ...(getChartOptions(hardwareCustomTooltip).xaxis || {}),
  //     categories: ['Previous Week', 'Last Week']
  //   }
  // };

//   const defaultCraftedCocktailsStat = {
//   title: "CRAFTED COCKTAILS",
//   total: 0,
//   chartType: "bar",
//   chartHeight: 60,
//   chartData: [
//     {
//       name: "Cocktails",
//       data: [
//         { x: "Previous Week", y: 0 },
//         { x: "Last Week", y: 0 }
//       ]
//     }
//   ],
//   info: [
//     { icon: "fa fa-chart-line fa-fw", text: "0 - Previous Week", tooltip: "Previous Week" },
//     { icon: "fa fa-chart-bar fa-fw", text: "0 - Last Week", tooltip: "Last Week" },
//     { icon: "fa fa-percent fa-fw", text: "Change: 0%", tooltip: "Change" },
//     { icon: "fa fa-percent fa-fw", text: "Bounce Rate: 0%", tooltip: "Bounce Rate" }
//   ]
// };

  function renderMap() {
    const inverse = getComputedStyle(document.body).getPropertyValue('--bs-inverse').trim();
    // const themeColor = getComputedStyle(document.body).getPropertyValue('--bs-theme').trim();
    const map = document.getElementById('world-map');
    const mapElm = document.querySelectorAll('.jvm-tooltip');

    if (map) {
      mapElm.forEach((el) => el.remove());
      map.innerHTML = '';
      new jsVectorMap({
        selector: '#world-map',
        map: 'world',
        zoomButtons: true,
        normalizeFunction: 'polynomial',
        hoverOpacity: 0.5,
        hoverColor: false,
        zoomOnScroll: false,
        series: { regions: [{ normalizeFunction: 'polynomial' }] },
        labels: { markers: { render: (marker) => marker.name } },
        focusOn: { x: 0.5, y: 0.5, scale: 1 },
        markerStyle: { initial: { fill: themeColor, stroke: 'none', r: 5 }, hover: { fill: themeColor } },
        regionStyle: { 
          initial: { fill: inverse, fillOpacity: 0.35, stroke: 'none', strokeWidth: 0.4, strokeOpacity: 1 }, 
          hover: { fillOpacity: 0.5 } 
        },
        backgroundColor: 'transparent'
      });
    }
  }

  useEffect(() => {
    fetch('/assets/data/dashboard/stats.json')
      .then((res) => res.json())
      .then((result) => setStatsData(result));
    fetch('/assets/data/dashboard/server.json')
      .then((res) => res.json())
      .then((result) => setServerData(result));
    fetch('/assets/data/dashboard/country.json')
      .then((res) => res.json())
      .then((result) => setCountryData(result));
    fetch('/assets/data/dashboard/source.json')
      .then((res) => res.json())
      .then((result) => setSourceData(result));
    fetch('/assets/data/dashboard/source-chart.json')
      .then((res) => res.json())
      .then((result) => setSourceChartData(result));
    fetch('/assets/data/dashboard/product.json')
      .then((res) => res.json())
      .then((result) => setProductData(result));
    fetch('/assets/data/dashboard/activity-log.json')
      .then((res) => res.json())
      .then((result) => setActivityLogData(result));

    renderMap();

    document.addEventListener('theme-reload', () => {
      setChartOptions(getChartOptions());
      renderMap();
    });
    // eslint-disable-next-line
  }, []);

  //ORDERS SPECIFIC FUNCTIONALITY

    // const [orderActivePeriod, setOrderActivePeriod] = useState("current");

    // // Returns the header label based on the active orders period.
    // const getOrderActivePeriodLabel = (period) => {
    //   switch (period) {
    //     case "current":
    //       return "Current Month's";
    //     case "previous":
    //       return "Previous Month's";
    //     case "three":
    //       return "Last 3 Months";
    //     case "six":
    //       return "Last 6 Months";
    //     default:
    //       return "";
    //   }
    // };

      // Calculates the date range based on the selected orders period.
  // const computeOrderDateRange = (period) => {
  //   const now = new Date();
  //   let from, to;
  //   switch (period) {
  //     case "current":
  //       // First and last day of the current month.
  //       from = new Date(now.getFullYear(), now.getMonth(), 1);
  //       to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  //       break;
  //     case "previous":
  //       // Previous month.
  //       let prevMonth = now.getMonth() - 1;
  //       let yearForPrev = now.getFullYear();
  //       if (prevMonth < 0) {
  //         prevMonth = 11;
  //         yearForPrev = now.getFullYear() - 1;
  //       }
  //       from = new Date(yearForPrev, prevMonth, 1);
  //       to = new Date(yearForPrev, prevMonth + 1, 0, 23, 59, 59);
  //       break;
  //     case "three":
  //       // From the first day 2 months before current month (covering 3 months total).
  //       from = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  //       to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  //       break;
  //     case "six":
  //       // From the first day 5 months before current month (covering 6 months total).
  //       from = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  //       to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  //       break;
  //     default:
  //       // Default to current month.
  //       from = new Date(now.getFullYear(), now.getMonth(), 1);
  //       to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  //   }
  //   return { dateFrom: from.toISOString(), dateTo: to.toISOString() };
  // };


  // Whenever the orderActivePeriod changes, fetch orders for that period.
  // useEffect(() => {
  //   const { dateFrom, dateTo } = computeOrderDateRange(orderActivePeriod);
  //   dispatch(fetchOrders( dateFrom, dateTo ));
  // }, [orderActivePeriod, dispatch]);

    // Styles matching the dashboard buttons
    // const activeButtonStyle = {
    //   backgroundColor: themeColor,
    //   border: `1px solid ${themeColor}`,
    //   color: "#fff",
    //   padding: "0.5rem 1rem",
    //   margin: "0.5rem",
    //   cursor: "pointer",
    //   borderRadius: "0.25rem",
    // };
  
    // const inactiveButtonStyle = {
    //   backgroundColor: "transparent",
    //   border: `1px solid ${themeColor}`,
    //   color: themeColor,
    //   padding: "0.5rem 1rem",
    //   margin: "0.5rem",
    //   cursor: "pointer",
    //   borderRadius: "0.25rem",
    // };



// -------------------------------
// ACTIVE VS NEW USERS (Compare Users)
// -------------------------------

// Local state for frequency (tab) selection
const [compareUsersTab, setCompareUsersTab] = useState("daily");

// Use two states for the limit:
//   • compareUsersLimitInput is updated immediately as the user types.
//   • compareUsersLimit is the debounced value used for fetching data.
const [compareUsersLimitInput, setCompareUsersLimitInput] = useState("50");
const [compareUsersLimit, setCompareUsersLimit] = useState("50");

// onChange handler for the limit input.
// (Renamed to handleLimitChangeUsers to avoid conflicts.)
const handleLimitChangeUsers = (value) => {
  setCompareUsersLimitInput(value);
};

// A useEffect to debounce changes in compareUsersLimitInput.
// This effect will update compareUsersLimit only after 1 second of inactivity.
useEffect(() => {
  const timer = setTimeout(() => {
    setCompareUsersLimit(compareUsersLimitInput);
  }, 1000); // 1 second debounce delay

  return () => clearTimeout(timer);
}, [compareUsersLimitInput]);

// Local state for the chart configuration.
const [newVsActiveChartOptions, setNewVsActiveChartOptions] = useState({});
const [newVsActiveChartSeries, setNewVsActiveChartSeries] = useState([]);

// Define an array of frequency tabs.
const compareUsersTabs = [
  { id: "daily", label: "Daily" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "yearly", label: "Yearly" },
];

// Use Redux to retrieve the compare users data from your dashboard slice.
// (Extend this for other frequencies as needed.)
const { 
    compare_users_day_info, 
    loading_compare_users_day, 
    compare_users_day_error,
    compare_users_week_info, loading_compare_users_week, compare_users_week_error,
    compare_users_month_info, loading_compare_users_month, compare_users_month_error,
    compare_users_year_info, loading_compare_users_year, compare_users_year_error } = useSelector((state) => state.dashboard);

// Combined useEffect to dispatch the API call when either the frequency tab or the debounced limit changes.
// The effect reads the debounced compareUsersLimit.
// (If the trimmed limit is empty, we don’t dispatch.)
useEffect(() => {
  const trimmedLimit = compareUsersLimit.trim();
  if (!trimmedLimit) return;
  
  if (compareUsersTab === "daily") {
    dispatch(fetchCompareUsersDay(trimmedLimit));
  } else if (compareUsersTab === "weekly") {
   dispatch(fetchCompareUsersWeek(trimmedLimit));
  } else if (compareUsersTab === "monthly") {
   dispatch(fetchCompareUsersMonth(trimmedLimit));
  } else if (compareUsersTab === "yearly") {
   dispatch(fetchCompareUsersYear(trimmedLimit));
  }
}, [compareUsersTab, compareUsersLimit, dispatch]);

//Feed Chart with the selected Dat
const dataForChart = 
  compareUsersTab === "daily" ? compare_users_day_info :
  compareUsersTab === "weekly" ? compare_users_week_info :
  compareUsersTab === "monthly" ? compare_users_month_info : 
  compare_users_year_info;

// Data transformation: Build chart options and series once data updates.
// (Also sorting the data may help with presentation.)
useEffect(() => {
  const toneColor = lighten(themeColor1, 0.5);
  if (Array.isArray(dataForChart)) {
   
    const sortedData = [...dataForChart].sort((a, b) => {
      // Correctly split based on the format mm/dd/yyyy
      const [monthA, dayA, yearA] = a.time_interval.split("/").map(Number);
      const [monthB, dayB, yearB] = b.time_interval.split("/").map(Number);

      // Create Date objects and subtract in reversed order for descending sort
        return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
      // return new Date(yearB, monthB - 1, dayB) - new Date(yearA, monthA - 1, dayA);
    });
    const categories = sortedData.map(item => item.time_interval);
    const newUsersData = sortedData.map(item => item.newUsers);
    const activeUsersData = sortedData.map(item => item.activeUsers);

    setNewVsActiveChartOptions({
      chart: {
        type: "line",
        id: "compareUsersLineChart",
        toolbar: { show: false },
      },
      xaxis: { 
        categories,
        tickAmount: 10,
        labels: {
          rotate: -45,
          hideOverlappingLabels: true,
        },
      },
      stroke: { curve: "smooth", width: 1 },
      tooltip: { x: { show: true } },
      colors: [ themeColor1, toneColor ]
    });

    setNewVsActiveChartSeries([
      { name: "New Users", data: newUsersData },
      { name: "Active Users", data: activeUsersData },
    ]);
  }
}, [dataForChart]);


// Retrieve theme color from CSS for styling.


// const activeStyle = {
//   backgroundColor: themeColor,
//   border: `1px solid ${themeColor}`,
//   color: "#fff",
//   padding: "0.5rem 1rem",
//   margin: "0.5rem",
//   cursor: "pointer",
//   borderRadius: "0.25rem",
// };

// const inactiveStyle = {
//   backgroundColor: "transparent",
//   border: `1px solid ${themeColor}`,
//   color: themeColor,
//   padding: "0.5rem 1rem",
//   margin: "0.5rem",
//   cursor: "pointer",
//   borderRadius: "0.25rem",
// };


// Calculate Total Users Number (Active - New) based on the selected dataset
const totalNewUsers = Array.isArray(dataForChart)
  ? dataForChart.reduce((sum, item) => sum + Number(item.newUsers || 0), 0)
  : 0;

const totalActiveUsers = Array.isArray(dataForChart)
  ? dataForChart.reduce((sum, item) => sum + Number(item.activeUsers || 0), 0)
  : 0;
// For Recipes List
const [currentRecipesLimit, setCurrentRecipesLimit] = useState("10");
const { user_recipes_info, loading_user_recipes } = useSelector((state) => state.dashboard);
const { 
  brand_recipes_info, 
  loading_brand_recipes, 
  brand_recipes_error 
} = useSelector((state) => state.dashboard);

const { 
  curated_recipes_info, 
  loading_curated_recipes, 
  curated_recipes_error 
} = useSelector((state) => state.dashboard);

// const {
//   influencer_recipes_info,
//   influencer_recipes_error,
//   loading_influencer_recipes
// } = useSelector((state) => state.dashboard); 

/// For the new User Recipes list
const [userRecipesActiveTab, setUserRecipesActiveTab] = useState("users");
const [userRecipesLimit, setUserRecipesLimit] = useState("10");

// Immediate effect: runs only on active tab changes (and on mount),
// using the current userRecipesLimit value available at that moment.
useEffect(() => {
  if (userRecipesActiveTab === "users") {
    dispatch(fetchUserRecipes("desc", 0, startTime, endTime, userRecipesLimit));
  } else if (userRecipesActiveTab === "brand") {
    dispatch(fetchBrandRecipes("desc", 0, startTime, endTime, userRecipesLimit));
  } else if (userRecipesActiveTab === "curated") {
    dispatch(fetchCuratedRecipes("desc", 0, startTime, endTime, userRecipesLimit));
  } else if (userRecipesActiveTab === "all") {
    dispatch(fetchLatestRecipes("all", userRecipesLimit));
  }
}, [userRecipesActiveTab, dispatch, startTime, endTime]);

// Debounced effect: When the user types a new limit, wait 1 second before re-dispatching.
// This prevents an API call on every keystroke.
useEffect(() => {
  const timer = setTimeout(() => {
    if (userRecipesActiveTab === "users") {
      dispatch(fetchUserRecipes("desc", 0, startTime, endTime, userRecipesLimit));
    } else if (userRecipesActiveTab === "brand") {
      dispatch(fetchBrandRecipes("desc", 0, startTime, endTime, userRecipesLimit));
    } else if (userRecipesActiveTab === "curated") {
      dispatch(fetchCuratedRecipes("desc", 0, startTime, endTime, userRecipesLimit));
    } else if (userRecipesActiveTab === "all") {
      dispatch(fetchLatestRecipes("all", userRecipesLimit));
    }
  }, 1000);
  return () => clearTimeout(timer);
}, [userRecipesLimit,  dispatch]);


// Compute the recipes data based on the active tab:
const recipesData =
  userRecipesActiveTab === "users"
    ? (user_recipes_info || [])
    : userRecipesActiveTab === "brand"
    ? (brand_recipes_info || [])
    : userRecipesActiveTab === "curated"
    ? (curated_recipes_info || [])
    : userRecipesActiveTab === "all"
    ? (latest_recipes_info || [])
    : [];

// Similarly, compute the loading flag:
const recipesLoading =
  userRecipesActiveTab === "users"
    ? loading_user_recipes
    : userRecipesActiveTab === "brand"
    ? loading_brand_recipes
    : userRecipesActiveTab === "curated"
    ? loading_curated_recipes
    : userRecipesActiveTab === "all"
    ? loading_latest_recipes
    : false;

// The handler for limit changes (shared across tabs)
const handleUserRecipesLimitChange = (value) => {
  setUserRecipesLimit(value);
};

//Playlist Components//
 // State for active tab and limit (as strings to match the other instance)
const [mixlistsActiveTab, setMixlistsActiveTab] = useState("mixlists");
const [mixlistsLimit, setMixlistsLimit] = useState("10");

  // Retrieve the mixlists-related data from Redux
  const {
    playlist_mixlists_info,
    loading_playlist_mixlists,
    brand_mixlists_info,
    loading_brand_mixlists,
    influencer_mixlists_info,
    loading_influencer_mixlists
  } = useSelector((state) => state.dashboard);

  const isInitialMountMixlists = useRef(true);

  // Immediate effect: fetch data on mount or when the active tab changes
  useEffect(() => {
    if (mixlistsActiveTab === "mixlists") {
      dispatch(
        fetchPlaylistMixlists("desc", 3, startTime, endTime, mixlistsLimit)
      );
    } else if (mixlistsActiveTab === "brands") {
      dispatch(
        fetchBrandMixlists("desc", 3, startTime, endTime, mixlistsLimit)
      );
    } else if (mixlistsActiveTab === "influencers") {
      dispatch(
        fetchInfluencerMixlists("desc", 3, startTime, endTime, mixlistsLimit)
      );
    }
    // Note: mixlistsLimit is included here so that on initial load the current limit is used.
  }, [mixlistsActiveTab, dispatch, startTime, endTime, mixlistsLimit]);

 // Debounced effect: re-fetch when the limit changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isInitialMountMixlists.current) {
        isInitialMountMixlists.current = false;
        return; // Skip the debounced call on the initial mount.
      }
      if (mixlistsActiveTab === "mixlists") {
        dispatch(fetchPlaylistMixlists("desc", 3, startTime, endTime, mixlistsLimit));
      } else if (mixlistsActiveTab === "brands") {
        // console.log('Debounced Effect:', brand_mixlists_info);
        dispatch(fetchBrandMixlists("desc", 3, startTime, endTime, mixlistsLimit));
      } else if (mixlistsActiveTab === "influencers") {
        dispatch(fetchInfluencerMixlists("desc", 3, startTime, endTime, mixlistsLimit));
      }
    }, 1000);
  
    return () => clearTimeout(timer);
  }, [mixlistsLimit, dispatch, startTime, endTime]);

 // Compute the data and loading flag based on the active tab
 let mixlistsData = [];
 let mixlistsLoading = false;

 if (mixlistsActiveTab === "mixlists") {
  // console.log('Active Tab Changed:', playlist_mixlists_info)
   mixlistsData = playlist_mixlists_info || [];
   mixlistsLoading = loading_playlist_mixlists;
 } else if (mixlistsActiveTab === "brands") {
   mixlistsData = brand_mixlists_info || [];
   mixlistsLoading = loading_brand_mixlists;
 } else if (mixlistsActiveTab === "influencers") {
   mixlistsData = influencer_mixlists_info || [];
   mixlistsLoading = loading_influencer_mixlists;
 }

 // Handler for limit change (shared across tabs)
 const handleMixlistsLimitChange = (value) => {
   setMixlistsLimit(value);
 };


//OLD IMPLEMENTATION
//  //TOTAL REGISTERED USERS
//  const [isTotalUserExpanded, setIsTotalUserExpanded] = useState(false);

//  const handleTotalUserExpand = () => {
//   setIsTotalUserExpanded(!isTotalUserExpanded);
// };

//  const TotalUserExpand = (
//   <>
//     <h3>TOTAL REGISTERED USER</h3>
//     <div className="row">
//       {data && data.userActive && (
//         <h2 className="col-xl-3">
//           {data.userActive}
//           {/* 1.98 M */}
//         </h2>
//       )}

//       <div className="col-md-2">
//         <label htmlFor="date">Date</label>
//         <Select
//           options={dateOptions}
//           value={selectedDateOption}
//           onChange={handleDateOptionChange}
//           classNamePrefix="react-select"
//         />
//         <div className="col-xl-9">
//           {customDateRange && (
//             <>
//               <label>From:</label>
//               <DatePicker
//                 selected={startDate}
//                 onChange={handleChangeStart}
//                 className="form-control"
//               />
//               <br></br>
//               <label>To:</label>
//               <DatePicker
//                 selected={endDate}
//                 onChange={handleChangeEnd}
//                 className="form-control"
//               />
//             </>
//           )}
//         </div>
//       </div>

//       <div className="col-md-2">
//         <label htmlFor="age">Age</label>
//         <Select
//           options={AgeOptions}
//           value={selectedAgeOption}
//           onChange={handleAgeOptionChange}
//           classNamePrefix="react-select"
//         />
//       </div>
//       <div className="col-md-2">
//         <label htmlFor="location">Location</label>
//         <select className="form-control" id="location">
//           <option value="all">All</option>
//         </select>
//       </div>

//       <div className="col-md-2 mt-3 pt-1">
//         <button className="btn btn-secondary">
//           <i className="fas fa-search"></i>
//         </button>
//       </div>
//     </div>
//     <div className="col-lg-12 mb-1 mb-lg-0">
//       <Chart
//         type="bar"
//         options={TRUbarChartOptions}
//         series={TRUbarChartData}
//         height="600"
//       />
//     </div>

//     {data && (
//       <h5>
//       {data2 && data2.userPreviuseWeek > 0 ? (
//   data2.userWeek !== data2.userPreviuseWeek ? (
//     <>
//       <i
//         className={`fas ${
//           data2.userWeek > data2.userPreviuseWeek
//             ? "fa-angle-up"
//             : "fa-angle-down"
//         }`}
//       ></i>
//       &nbsp;
//       {Math.abs(
//         ((data2.userWeek - data2.userPreviuseWeek) /
//           data2.userPreviuseWeek) *
//           100
//       ).toFixed(2)}{" "}
//       %{" "}
//       {data2.userWeek > data2.userPreviuseWeek
//         ? "more"
//         : "less"}{" "}
//       than last week
//     </>
//   ) : (
//     "No change from last week"
//   )
// ) : (
//   "Data unavailable"
// )}
//       </h5>
//     )}
//     {data && (
//       <h5>
//         <i className="fas fa-user"></i> {data.userlastWeekUser} new visitors
//       </h5>
//     )}
//   </>
// );

//NEW IMPLEMENTATION FOR EXPANDING TOTAL REGISTERED USERS//
// --- At the top of your Dashboard component ---
// … other imports remain unchanged …

// Option arrays for time dimension and age
const timeDimensionOptions = [
  { value: '0', label: 'Yearly' },
  { value: '1', label: 'Monthly' },
  { value: '2', label: 'Weekly' },
  { value: '5', label: 'Daily' },
  { value: '3', label: 'Custom' }
];

const ageOptions = [
  { value: '0-18', label: '0-18' },
  { value: '18-35', label: '18-35' },
  { value: '35-60', label: '35-60' },
  { value: '60+', label: '60+' }
];

// Local state for this card
// const [isTotalUserExpanded, setIsTotalUserExpanded] = useState(false);
// const [selectedTimeDimension, setSelectedTimeDimension] = useState(timeDimensionOptions[0]);
// For custom date range if "Custom" is chosen:
// const [customFromDate, setCustomFromDate] = useState(new Date());
// const [customToDate, setCustomToDate] = useState(new Date());
// For age range:
// const [ageFrom, setAgeFrom] = useState('');
// const [ageTo, setAgeTo] = useState('');

// Make sure these come from your Redux store
// const { stats_q1 } = useSelector((state) => state.dashboard); 
const { loading_user_tile_info, user_tile_info } = useSelector((state) => state.dashboard);

  // Expanded top cards
  //Crafted Cocktails

  const craftedPeriodOptions = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "year", label: "Yearly" },
  // { value: "custom", label: "Custom" },
];

  const [craftedCustomFrom, setCraftedCustomFrom] = useState(new Date());
  const [craftedCustomTo, setCraftedCustomTo] = useState(new Date());

  // const [showCraftedModal, setShowCraftedModal] = useState(false);
    const {
    crafted_cocktails_expand_info,
    crafted_cocktails_expand_error,
    loading_crafted_cocktails_expand
  } = useSelector((state) => state.dashboard);
    
  const [isCraftedCocktailExpanded, setIsCraftedCocktailExpanded] = useState(false);
  const [craftedPeriod, setCraftedPeriod] = useState(craftedPeriodOptions[2]); // "Monthly"
  const [craftedPeriodOption, setCraftedPeriodOption] = useState({ value: "week", label: "Weekly" });
  // const [craftedStartDate, setCraftedStartDate] = useState(new Date());
  // const [craftedEndDate, setCraftedEndDate] = useState(new Date());
  // const [isCraftedCocktailModalOpen, setIsCraftedCocktailModalOpen] = useState(false);

  // Prepare Chart Data for Expandable Crafted Cocktails Section
  const [craftedChartOptions, setCraftedChartOptions] = useState({});
  const [craftedChartData, setCraftedChartData] = useState([]);

  const [craftedLimitInput, setCraftedLimitInput] = useState(20);
  const [craftedLimit, setCraftedLimit] = useState(20);

useEffect(() => {
  const handler = setTimeout(() => {
    const value = Number(craftedLimitInput);
    if (!isNaN(value) && value > 0) setCraftedLimit(value);
  }, 1000);
  return () => clearTimeout(handler);
}, [craftedLimitInput]);

useEffect(() => {
  const periodVal = typeof craftedPeriod === "string" ? craftedPeriod : craftedPeriod.value;
  if (periodVal === "custom") {
    dispatch(fetchCraftedCocktailsExpand(
      "custom",
      craftedLimit,
      null, null,
      craftedCustomFrom.toISOString(),
      craftedCustomTo.toISOString()
    ));
  } else {
    dispatch(fetchCraftedCocktailsExpand(periodVal, craftedLimit));
  }
}, [craftedPeriod, craftedLimit, craftedCustomFrom, craftedCustomTo, dispatch]);


// Expanded Crafted Cocktails View
function CraftedCocktailsModalContent({
  total,
  loading,
  data,
  chartOptions,
  chartSeries,
  craftedPeriod,
  setCraftedPeriod,
  craftedLimit,          // committed value from parent
  setCraftedLimit,       // parent setter
  craftedCustomFrom,
  setCraftedCustomFrom,
  craftedCustomTo,
  setCraftedCustomTo,
  themeColor
}) {
  // --- Local mirrors (no parent updates until Apply is clicked)
  const [localPeriod, setLocalPeriod] = React.useState(craftedPeriod);
  const [localFrom, setLocalFrom] = React.useState(craftedCustomFrom);
  const [localTo, setLocalTo] = React.useState(craftedCustomTo);
  const [localLimit, setLocalLimit] = React.useState(craftedLimit);

  // Sync locals when parent props change (e.g., modal reopen or external updates)
  React.useEffect(() => { setLocalPeriod(craftedPeriod); }, [craftedPeriod]);
  React.useEffect(() => { setLocalFrom(craftedCustomFrom); }, [craftedCustomFrom]);
  React.useEffect(() => { setLocalTo(craftedCustomTo); }, [craftedCustomTo]);
  React.useEffect(() => { setLocalLimit(craftedLimit); }, [craftedLimit]);

  // Apply all filters at once
  const handleApply = () => {
    // Basic guard on limit
    const numericLimit = Number(localLimit);
    if (!isNaN(numericLimit) && numericLimit > 0) {
      setCraftedLimit(numericLimit);
    }
    setCraftedPeriod(localPeriod);
    setCraftedCustomFrom(localFrom);
    setCraftedCustomTo(localTo);
    // Parent should (re)query using these committed values.
  };

  const totalCrafted = Array.isArray(data)
    ? data.reduce((acc, item) => acc + Number(item.value || 0), 0)
    : 0;

  // --- Render unchanged chart/markup except for using local mirrors & the Apply button
  return (
    <div>
      <h3 style={{ color: themeColor, fontWeight: 700 }}>CRAFTED COCKTAILS</h3>
      <div style={{
        fontSize: 18,
        fontWeight: 500,
        marginBottom: 24,
        color: themeColor
      }}>{'TOTAL EVER CRAFTED: '+total}</div>

      <div className="row mb-4 justify-content-center align-items-end">
        <div className="col-md-3">
          <label className="form-label" style={{ color: themeColor1 }}>Date Dimension</label>
          <Select
            options={craftedPeriodOptions}
            value={localPeriod}
            onChange={setLocalPeriod}
            styles={selectStyles}
            classNamePrefix="react-select"
          />
        </div>

        {localPeriod.value === "custom" && (
          <>
            <div className="col-md-3">
              <label className="form-label" style={{ color: themeColor }}>From</label>
              <DatePicker
                selected={localFrom}
                onChange={setLocalFrom}
                className="form-control"
                style={{ borderColor: themeColor, color: themeColor }}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label" style={{ color: themeColor }}>To</label>
              <DatePicker
                selected={localTo}
                onChange={setLocalTo}
                className="form-control"
                style={{ borderColor: themeColor, color: themeColor1 }}
              />
            </div>
          </>
        )}

        <div className="col-md-2">
          <label className="form-label" style={{ color: themeColor1 }}>Limit</label>
          <input
            type="number"
            className="form-control"
            value={localLimit}
            min={1}
            max={100}
            onChange={e => setLocalLimit(e.target.value)}
          />
        </div>

        <div className="col-md-1 align-items-end">
          <button className="btn btn-secondary ms-auto" onClick={handleApply}>
            <i className="fas fa-search"></i>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center p-3">
          <div className="spinner-border" style={{ color: themeColor1 }} />
        </div>
      ) : data && data.length ? (
        <Chart
          type="line"
          options={{
            ...chartOptions,
            grid: {
              ...(chartOptions.grid || {}),
              padding: {
                ...(chartOptions.grid?.padding || {}),
                bottom: 80,      // room for rotated labels
                left: 6,         // small side padding
                right: 10
              }
            },
            xaxis: {
              categories: data.map(i => i.label),
              tickPlacement: "on",          // <-- removes left/right gap
              labels: {
                rotate: -60,
                rotateAlways: true,
                hideOverlappingLabels: true,
                trim: true,
                maxHeight: 100,
                offsetY: 8,
                style: {
                  fontFamily: "Chakra Petch",
                  fontSize: "12px",
                  colors: Array(data.length).fill(themeColor1 ?? themeColor),
                },
              },
            },
            yaxis: { /* unchanged */ },
            dataLabels: { enabled: false },
          }}
          series={chartSeries}
          height={400}
        />
      ) : (
        <div>No data available.</div>
      )}

      {/* Bottom left total for the current charted data */}
      <div style={{
        position: "relative",
        left: 25,
        bottom: 10,
        padding: "12px 0 0 4px",
        fontWeight: 600,
        fontSize: 15,
        color: themeColor,
        zIndex: 2
      }}>
        TOTAL IN CHART: <span style={{ color: themeColor }}>{totalCrafted}</span>
      </div>
    </div>    
  );
}


const selectStyles = {
  control: (base, state) => ({
    ...base,
    // borderColor: themeColor1,
    color: themeColor1,
    backgroundColor: "none",
    minHeight: 36,
    boxShadow: "none",
    "&:hover": { borderColor: themeColor1 }
  }),
  singleValue: base => ({
    ...base,
    color: themeColor1
  }),
  menu: base => ({
    ...base,
    // backgroundColor: themeColor1
  }),
  option: (base, state) => ({
    ...base,
    color: themeColor1,
    // backgroundColor: state.isSelected ? "#222" : "#181c24",
    // "&:hover": { backgroundColor: "#222" }
  })
};


  const craftedCocktailsModalContent = (
    <CraftedCocktailsModalContent
      total={stats_q3?.make}
      loading={loading_crafted_cocktails_expand}
      data={crafted_cocktails_expand_info}
      chartOptions={craftedChartOptions}
      chartSeries={craftedChartData}
      craftedPeriod={craftedPeriod}
      setCraftedPeriod={setCraftedPeriod}
      
      craftedLimit={craftedLimitInput}
      setCraftedLimit={setCraftedLimitInput}
      craftedCustomFrom={craftedCustomFrom}
      setCraftedCustomFrom={setCraftedCustomFrom}
      craftedCustomTo={craftedCustomTo}
      setCraftedCustomTo={setCraftedCustomTo}
      themeColor={themeColor}
    />
  );



    function safeFixed(val, digits = 2) {
      if (val == null || isNaN(val)) return "0.00";
      return Number(val).toFixed(digits);
    }

    // F-24: A bounce rate is a ratio in [0,1] → percentage in [0,100].
    // Observed production value 1.3452 → UI printed 134.52% (impossible).
    // The source field (stats_q3.newMakeBounceRate) appears to be a
    // week-over-week ratio, not a true bounce rate. Clamp defensively so
    // the tile never displays > 100%. TODO: rename the field upstream in
    // dashboard.model.js once the correct semantic is confirmed.
    function safePct(val, digits = 2) {
      if (val == null || isNaN(val)) return "0.00";
      const clamped = Math.max(0, Math.min(100, Number(val) * 100));
      return clamped.toFixed(digits);
    }

    const cocktailStats = {
  title: "CRAFTED COCKTAILS",
  total: stats_q3?.make,
  chartType: "bar",
  chartHeight: 60,
  chartData: [
    {
      name: "Cocktails",
      data: [
        { x: "Previous Week", y: Number(stats_q3?.previuseWeekMake) || 0 },
        { x: "Current Week", y: Number(stats_q3?.lastWeekMake) || 0 },
      ]
    }
  ],
  info: [
    { icon: "fa fa-chart-line fa-fw", text: `${stats_q3?.previuseWeekMake} - Previous Week`, tooltip: "Previous Week" },
    { icon: "fa fa-chart-bar fa-fw", text: `${stats_q3?.lastWeekMake} - Current Week`, tooltip: "Current Week" },
    { icon: "fa fa-percent fa-fw", text: `Change: `, text2: `${safeFixed(stats_q3?.makeMoreThenPrevWeek)}%`, color: stats_q3?.makeMoreThenPrevWeek >= 0 ? 'green' : 'red' },
    { icon: "fa fa-percent fa-fw", text: `Bounce Rate: ${safePct(stats_q3?.newMakeBounceRate)}%` } // F-24: clamped [0,100]
  ]
};
  const customTooltipCrafted = {
    bar: {
      x: {
        show: true,
        formatter: (val, opts) => {
          const labels = ["Previous Week", "Current Week"];
          return typeof opts.dataPointIndex !== "undefined"
            ? labels[opts.dataPointIndex]
            : val;
        }
      },
      y: {
        formatter: (val) => `${val}`
      }
    }
  };

  const chartOptionsCrafted = getChartOptions(customTooltip);

  // The modal content, e.g. a details chart/table, filters, etc
  const modalContent = (
    <CraftedCocktailsModalContent
      details={crafted_cocktails_expand_info}
      loading={loading_crafted_cocktails_expand}
      chartOptions={craftedChartOptions}
      chartData={craftedChartData}
      // Pass other props as needed
    />
  );

  const chartColor = themeColor1 || "#08b0af";

  function getYAxisLabelColor() {
    // Example: use a CSS variable you set for the theme, or read from body
    const isDark = document.body.classList.contains('theme-dark'); // or use your logic!
    return isDark ? "#e0e0e0" : "#444";
  }


  useEffect(() => {
    if (Array.isArray(crafted_cocktails_expand_info) && crafted_cocktails_expand_info.length > 0) {
      const categories = crafted_cocktails_expand_info.map(row => row.label);
      const values = crafted_cocktails_expand_info.map(row => row.value);

      setCraftedChartOptions({
        chart: { type: "line", id: "craftedCocktailsExpandChart", toolbar: { show: false } },
        xaxis: {
          categories,
          labels: { 
            rotate: -45,
            style: { colors: themeColor1, fontWeight: 600 }
          },
          title: {
            // text: "Period",
            style: { color: themeColor1, fontWeight: 700, fontSize: "14px" }
          }
        },
        yaxis: {
          labels: {
            style: { colors: getYAxisLabelColor, fontWeight: 600 }
          },
          // title: {
          //   text: "Crafted Cocktails",
          //   style: { color: themeColor1, fontWeight: 700, fontSize: "14px" }
          // }
        },
        stroke: { curve: "smooth", width: 2 },
        tooltip: { x: { show: true } },
        colors: [chartColor],
      });

      setCraftedChartData([
        { name: "Crafted Cocktails", data: values }
      ]);
    } else {
      setCraftedChartOptions({});
      setCraftedChartData([]);
    }
  }, [crafted_cocktails_expand_info, chartColor, themeColor1]);

    const handleCraftedCocktailExpand = () => {
      setIsCraftedCocktailExpanded(!isCraftedCocktailExpanded);
      // On expand, optionally fire the fetch if you haven't already.
      // (Optional: only fetch if not loaded yet)
      if (!crafted_cocktails_expand_info.length && !loading_crafted_cocktails_expand) {
        dispatch(fetchCraftedCocktailsExpand("week", 20)); // Default: week/20
      }
    };

    const handleCraftedPeriodChange = (option) => {
      setCraftedPeriod(option.value);
      setCraftedPeriodOption(option);
      if (option.value !== "custom") {
        // Optionally fetch immediately for non-custom
        dispatch(fetchCraftedCocktailsExpand(option.value, craftedLimit));
      }
    };

    // EXPAND FOR HARDWARE CARD /////////////////////////////////////
     // Debounced limit
    /* NEW */ 
      function HardwareUsageModalContent({
        data, loading,
        hwPeriod, setHwPeriod,
        hwCustomFrom, setHwCustomFrom,
        hwCustomTo,   setHwCustomTo,
        hwDeviceFilter, setHwDeviceFilter,
        hwLimitInput,  setHwLimitInput,
        themeColor
      }) {
        // ------------------------
        // Local mirrors (no parent updates until Apply is clicked)
        // ------------------------
        const [localPeriod, setLocalPeriod] = useState(hwPeriod);
        const [localFrom, setLocalFrom] = useState(hwCustomFrom);
        const [localTo, setLocalTo] = useState(hwCustomTo);
        const [localDevice, setLocalDevice] = useState(hwDeviceFilter);
        const [localLimit, setLocalLimit] = useState(hwLimitInput);

        // Resync locals if parent props change (e.g., when modal reopens)
        useEffect(() => { setLocalPeriod(hwPeriod); }, [hwPeriod]);
        useEffect(() => { setLocalFrom(hwCustomFrom); }, [hwCustomFrom]);
        useEffect(() => { setLocalTo(hwCustomTo); }, [hwCustomTo]);
        useEffect(() => { setLocalDevice(hwDeviceFilter); }, [hwDeviceFilter]);
        useEffect(() => { setLocalLimit(hwLimitInput); }, [hwLimitInput]);

        // Apply button: push locals to parent setters in one shot
        const handleApplyFilters = () => {
          setHwPeriod(localPeriod);
          setHwCustomFrom(localFrom);
          setHwCustomTo(localTo);
          setHwDeviceFilter(localDevice);
          setHwLimitInput(localLimit);
          // Parent should (re)query using the applied filters.
        };

        // ------------------------
        // Chart opts & series (unchanged)
        // ------------------------
        const [opts, setOpts]     = useState({});
        const [series, setSeries] = useState([]);

        useEffect(() => {
          if (Array.isArray(data) && data.length) {
            const cats = data.map(r => r.label);
            const vals = data.map(r => Number(r.value));
            setOpts({
              chart: { type: 'line', toolbar: { show: false }, color: themeColor1,  },
              xaxis: { categories: cats, labels: { rotate: -45 }, labels: { style: { colors: themeColor, fontFamily: themeFont, fontSize: 10, } } },
              stroke: { curve: 'smooth', width: 2 }
            });
            setSeries([{ name: 'H/W Deployed', data: vals, color: themeColor, fontFamily: themeFont }]);
          } else {
            setOpts({}); setSeries([]);
          }
        }, [data, themeColor]);

        return (
          <div>
            <h3>HARDWARE DEPLOYED</h3>
            <div style={{ marginBottom:16, fontWeight:500, }}>
              TOTAL: {hardware_chart_tile_info?.totalDevices ?? 0}
            </div>

            <div className="row mb-4 justify-content-center align-items-end">
              <div className="col-md-3">
                <label style={{ color: themeColor }}>Date Dimension</label>
                <Select
                  options={hwPeriodOptions}
                  value={localPeriod}
                  onChange={setLocalPeriod}
                  classNamePrefix="react-select"
                />
              </div>

              {localPeriod?.value==='custom' && <>
                <div className="col-md-3">
                  <label style={{ color: themeColor }}>From</label>
                  <DatePicker
                    selected={localFrom}
                    onChange={setLocalFrom}
                    className="form-control"
                  />
                </div>
                <div className="col-md-3">
                  <label style={{ color: themeColor }}>To</label>
                  <DatePicker
                    selected={localTo}
                    onChange={setLocalTo}
                    className="form-control"
                  />
                </div>
              </>}

              <div className="col-md-3">
                <label style={{ color: themeColor }}>Device Filter</label>
                <Select
                  options={hwDeviceOptions}
                  value={localDevice}
                  onChange={setLocalDevice}
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col-md-2">
                <label style={{ color: themeColor }}>Limit</label>
                <input
                  type="number"
                  className="form-control"
                  value={localLimit}
                  onChange={e => setLocalLimit(e.target.value)}
                />
              </div>

              <div className="col-md-2 mt-3 pt-1">
                <button className="btn btn-secondary" onClick={handleApplyFilters}>
                  <i className="fas fa-search"></i>
                </button>
              </div>
            </div>

            {loading
              ? <div className="text-center py-4">
                  <div className="spinner-border" style={{ color: themeColor }} />
                </div>
              : series.length
                ? <Chart options={opts} series={series} type="line" height={350}/>
                : <div>No data</div>
            }
            <div style={{ marginBottom:10, fontWeight:500, }}>
              TOTAL IN CHART: {data?.reduce((s,r)=>s+Number(r.value),0)||0}
            </div>
          </div>
        );
      }


        // 1) Safely fall back if that slice is ever null/undefined:
  const summary = hardware_chart_tile_info ?? {};

  // 2) Parse out the three numbers (defaulting to 0):
  const prev = Number(summary.previousWeek   || 0);
  const curr = Number(summary.currentWeek    || 0);
  const total  = Number(summary.totalDevices   || 0);
  

  // 3) Compute derived percentages:
  // const change     = prev > 0
  //   ? (((curr - prev) / prev) * 100).toFixed(2) + '%'
  //   : '0%';
  // const percentOf  = tot  > 0
  //   ? ((curr / tot) * 100).toFixed(2) + '%'
  //   : '0%';

  // Safely compute percentages
const deltaPercent = prev > 0
  ? (((curr - prev) / prev) * 100).toFixed(2) + '%'
  : '0%';

const ofTotalPercent = total > 0
  ? ((curr / total) * 100).toFixed(2) + '%'
  : '0%';

  // 4) Finally build the stat object:
  const hardwareStat = {
    title:      'HARDWARE DEPLOYED',
    total:      total,
    chartType:  'bar',
    chartHeight: 60,
    chartData: [
      {
        name: 'Devices',
        data: [
          { x: 'Previous Week', y: prev },
          { x: 'Current Week',  y: curr }
        ]      }

    ],
    info: [
      { icon: 'fa fa-chart-line fa-fw', text: `${prev} prev`, tooltip: 'Previous Week' },
      { icon: 'fa fa-chart-bar fa-fw',  text: `${curr} curr`, tooltip: 'Current Week' },
      { icon: 'fa fa-percent fa-fw',    text: `Δ ${deltaPercent}`,   tooltip: 'Change vs. Previous' },
      { icon: 'fa fa-percent fa-fw',    text: `of total ${ofTotalPercent}`, tooltip: '% of Total' },
    ]
  };

      // ─── EXPAND FOR HARDWARE CARD ─────────────────────────────
      const [hwLimitInput,   setHwLimitInput]   = useState('20');
      const [hwLimit,        setHwLimit]        = useState('20');
      const [hwPeriod,       setHwPeriod]       = useState(hwPeriodOptions[1]);
      const [hwCustomFrom,   setHwCustomFrom]   = useState(new Date());
      const [hwCustomTo,     setHwCustomTo]     = useState(new Date());
      const [hwDeviceFilter, setHwDeviceFilter] = useState(hwDeviceOptions[0]);

      // debounce hwLimitInput → hwLimit
      useEffect(() => {
        const t = setTimeout(() => {
          const n = Number(hwLimitInput.trim());
          if (!isNaN(n) && n>0) setHwLimit(String(n));
        }, 1000);
        return () => clearTimeout(t);
      }, [hwLimitInput]);

      // fetch expanded stats whenever any filter changes
      useEffect(() => {
        const p = hwPeriod.value;
        if (p === 'custom') {
          dispatch(fetchHardwareUsageStats(
            p, hwLimit, hwDeviceFilter.value,
            hwCustomFrom.toISOString(), hwCustomTo.toISOString()
          ));
        } else {
          dispatch(fetchHardwareUsageStats(p, hwLimit, hwDeviceFilter.value));
        }
      }, [hwPeriod, hwLimit, hwDeviceFilter, hwCustomFrom, hwCustomTo, dispatch]);

      //Expanded Users View
      const comparePeriodOptions = [
        { value: 'day',   label: 'Daily'   },
        { value: 'week',  label: 'Weekly'  },
        { value: 'month', label: 'Monthly' },
        { value: 'year',  label: 'Yearly'  },
      ];

      const [cuPeriod,     setCuPeriod]     = useState(comparePeriodOptions[2]);  // default “Monthly”
      const [cuLimitInput, setCuLimitInput] = useState('12');
      const [cuLimit,      setCuLimit]      = useState('12');

      useEffect(() => {
        const t = setTimeout(() => {
          const s = String(cuLimitInput ?? '').trim();
          if (s) setCuLimit(s);
        }, 1000);
        return () => clearTimeout(t);
      }, [cuLimitInput]);

      useEffect(() => {
        dispatch(fetchCompareUsers(cuPeriod.value, cuLimit));
      }, [cuPeriod, cuLimit, dispatch]);

      const {
        compare_users:    cuData    = [],
        loading_compare_users: cuLoading,
        compare_users_error:   cuError,
      } = useSelector(state => state.dashboard);

      // base sparkline bar options
        const baseBar = getChartOptions().bar;

        // your dataLabels override
        const dataLabelsOverride = {
          enabled: true,
          style: {
            fontFamily: themeFont,
            fontSize: '14px',
            fontWeight: '700',
            colors: ['#fff'],
          }
        };

        // expanded modal chartOptions
const compareUsersExpandedOptions = {
  // 1) start from the same base bar options
  ...baseBar,

  // 2) turn off sparkline so axes & dataLabels show
  chart: {
    ...baseBar.chart,
    sparkline: { enabled: false },
  },

  // 3) carry over the x-axis categories & style
  xaxis: {
    ...(baseBar.xaxis || {}),
    categories: cuData.map(r => r.period),
    labels: {
      ...(baseBar.xaxis?.labels || {}),
      rotate: -45,
      style: {
        ...(baseBar.xaxis?.labels?.style || {}),
        fontFamily: themeFont,
      }
    }
  },

  // 4) tooltip number formatting stays the same
  tooltip: {
    ...baseBar.tooltip,
    y: {
      ...baseBar.tooltip.y,
      formatter: v => v.toLocaleString()
    }
  },

  // 5) dataLabels: put them on top, offset a bit, use theme font + color
  dataLabels: {
    enabled: true,
    position: 'top',
    offsetY: -10,
    style: {
      fontFamily: themeFont,
      fontSize: '14px',
      fontWeight: '600',
      colors: [ themeColor ],
    },
  },

  // 6) (optional) if you want a three-tone palette, you can still add:
  // colors: [ themeColor, lighten(themeColor,0.2), lighten(themeColor,0.5) ],
};


function CompareUsersModalContent({
  data = [],
  loading,
  period,
  setPeriod,
  limitInput,
  setLimitInput,
  themeColor
}) {
  const [localLimit, setLocalLimit] = useState(limitInput);
  const [localPeriod, setLocalPeriod] = useState(period);

  const themeFont = getComputedStyle(document.documentElement)
    .getPropertyValue('--bs-body-font-family')
    .trim();

  // Sync parent → local
  useEffect(() => { setLocalLimit(limitInput); }, [limitInput]);
  useEffect(() => { setLocalPeriod(period); }, [period]);

  // Apply all filters in one go
  const handleApply = () => {
    const n = Number(localLimit);
    if (!Number.isNaN(n) && n > 0) {
      setLimitInput(n);       // keep as number to avoid .trim() issues upstream
    }
    setPeriod(localPeriod);
  };

  // chart options & series (unchanged)
  const [opts, setOpts]     = useState({});
  const [series, setSeries] = useState([]);

  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) {
      setOpts({});
      setSeries([]);
      return;
    }

    const tone1 = lighten(themeColor, 0.2);
    const tone2 = lighten(themeColor, 0.5);

    const xLabelColors = data.map(() => themeColor); //  one color per x label

    setOpts({
      chart: { type: "bar", toolbar: { show: false }, sparkline: { enabled: false } },
      colors: [themeColor, tone1, tone2],

      xaxis: {
        categories: data.map((r) => r.period),
        labels: {
          rotate: -45,
          style: {
            fontFamily: themeFont,
            colors: xLabelColors, //  all x labels use themeColor
          },
        },
        axisBorder: { show: true, color: themeColor },
        axisTicks: { show: true, color: themeColor },
      },

      yaxis: {
        labels: {
          style: {
            fontFamily: themeFont,
            colors: themeColor, //  all y labels use themeColor
          },
        },
        axisBorder: { show: true, color: themeColor },
        axisTicks: { show: true, color: themeColor },
      },

      legend: {
        show: true,
        position: "bottom",
        fontFamily: themeFont,
        labels: {
          colors: themeColor,        // legend TEXT color
          useSeriesColors: false,    // don’t auto-color text per series
        },
      },

      plotOptions: { bar: { dataLabels: { position: "top" } } },

      dataLabels: {
        enabled: true,
        position: "top",
        offsetY: -20,
        style: {
          fontFamily: themeFont,
          fontSize: "10px",
          fontWeight: "600",
          colors: [themeColor],
        },
      },

      tooltip: { y: { formatter: (v) => v.toLocaleString() } },
    });


    setSeries([
      { name: 'Registered', data: data.map(r => r.registered_count) },
      { name: 'Active',     data: data.map(r => r.active_count) },
      { name: 'Non-Active', data: data.map(r => r.non_active_count) }
    ]);
  }, [data, themeColor, themeFont]);

  // Helpers for local period updates
  const handleLocalPeriodChange = (opt) => {
    setLocalPeriod({
      ...opt,
      customFrom: localPeriod?.customFrom,
      customTo:   localPeriod?.customTo,
    });
  };
  const handleLocalFromChange = (d) => setLocalPeriod(prev => ({ ...prev, customFrom: d }));
  const handleLocalToChange   = (d) => setLocalPeriod(prev => ({ ...prev, customTo: d }));

  return (
    <div>
      <h3>COMPARE USERS</h3>

      {/* centered controls, separate apply button */}
      <div className="d-flex justify-content-center align-items-end flex-wrap gap-3 mb-4">
        <div style={{ minWidth: 260 }}>
          <label className="form-label" style={{ color: themeColor }}>Date Dimension</label>
          <Select
            options={comparePeriodOptions}
            value={localPeriod}
            onChange={handleLocalPeriodChange}
            classNamePrefix="react-select"
          />
        </div>

        {localPeriod?.value === 'custom' && (
          <>
            <div style={{ minWidth: 220 }}>
              <label className="form-label" style={{ color: themeColor }}>From</label>
              <DatePicker
                selected={localPeriod?.customFrom}
                onChange={handleLocalFromChange}
                className="form-control"
              />
            </div>
            <div style={{ minWidth: 220 }}>
              <label className="form-label" style={{ color: themeColor }}>To</label>
              <DatePicker
                selected={localPeriod?.customTo}
                onChange={handleLocalToChange}
                className="form-control"
              />
            </div>
          </>
        )}

        <div style={{ minWidth: 180 }}>
          <label className="form-label" style={{ color: themeColor }}>Limit</label>
          <input
            type="number"
            className="form-control"
            min={1}
            value={localLimit}
            onChange={e => setLocalLimit(e.target.value)}
          />
        </div>

        {/* separate, small button block, still part of centered group */}
        <div className="d-flex align-items-end">
          <button className="btn btn-secondary" onClick={handleApply}>
            <i className="fas fa-search"></i>
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" style={{ color: themeColor }} />
        </div>
      ) : series.length ? (
        <Chart options={opts} series={series} type="bar" height={300} />
      ) : (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>No data</div>
      )}
    </div>
  );
}



/**
 * Lightens a hex colour by mixing it with white.
 * @param {string} hex  — e.g. "#08b0af"
 * @param {number} amt  — fraction between 0 (no change) and 1 (white)
 */
function lighten(hex, amt) {
  // strip “#” if present
  hex = hex.replace(/^#/, '');
  // parse r, g, b
  const num = parseInt(hex, 16);
  let r = (num >> 16) & 0xFF;
  let g = (num >> 8) & 0xFF;
  let b = num & 0xFF;
  // mix each channel towards 255 (white)
  r = Math.round(r + (255 - r) * amt);
  g = Math.round(g + (255 - g) * amt);
  b = Math.round(b + (255 - b) * amt);
  // reassemble and zero-pad
  const newHex = ((1 << 24) | (r << 16) | (g << 8) | b)
    .toString(16)
    .slice(1);
  return `#${newHex}`;
}

  // two lighter tones:
  const tone1 = lighten(themeColor1, 0.2); // 20% closer to white
  const tone2 = lighten(themeColor1, 0.5); // 50% closer to white
  // F-25: Use the latest bucket only rather than summing across all buckets.
  // Summing caused Active (181,460) to exceed Registered (180,657) because a
  // user active in month N is re-counted in month N+1. The tile's semantic is
  // "current snapshot" so we take the last element (most recent period).
  // TODO: if lifetime distinct actives are needed, add a /compare-users-distinct
  // endpoint with COUNT(DISTINCT user_id) over the full window.
  const cuLatest        = (cuData && cuData.length) ? cuData[cuData.length - 1] : { registered_count: 0, active_count: 0, non_active_count: 0 };
  const totalRegistered = cuLatest.registered_count;
  const totalActive     = cuLatest.active_count;
  const totalNonActive  = cuLatest.non_active_count;

// 1a) Stat object (just like hardwareStat)
// const compareUsersStat = {
//   title: 'USERS COMPARISON',
//   total:  cuData.reduce((sum,r)=>sum+r.registered_count,0),
//   chartType: 'bar',
//   chartHeight: 60,                       // ← your 60px
//   chartData: [
//     { name:'Registered', data: cuData.map(r=>r.registered_count) },
//     { name:'Active',     data: cuData.map(r=>r.active_count)   },
//     { name:'Non-Active', data: cuData.map(r=>r.non_active_count) },
//   ],
//   info: [
//     { icon: 'fa', text: ' '},
//     { icon:'fa fa-user-plus',  text:`Total Reg: ${totalRegistered}` },
//     { icon:'fa fa-user',       text:`Active: ${totalActive}` },
//     { icon:'fa fa-user-slash', text:`Non-Active: ${totalNonActive}` },
//   ]
// };

const compareUsersData = useSelector(state => state.dashboard.compare_users || []);

// 2) Pull your base bar-options then override its height:
const baseBarOpts = getChartOptions().bar;

// build the stat for TopCard
const compareUsersStat = {
  title: 'USERS COMPARISON',
  total: totalRegistered, // F-25: latest bucket, not cumulative sum
  chartType: 'bar',
  chartHeight: 60,
  chartData: [
    { name: 'Registered', data: cuData.map(r => r.registered_count) },
    { name: 'Active',     data: cuData.map(r => r.active_count)     },
    { name: 'Non-Active', data: cuData.map(r => r.non_active_count) },
  ],
  info: [
    { icon: 'fa', text: `` },
    { icon: 'fa fa-user-plus fa-fw', text: `${totalRegistered} - Registered (latest period)` },
    { icon: 'fa fa-user fa-fw',      text: `${totalActive} - Active (latest period)` },
    { icon: 'fa fa-user-slash fa-fw',text: `${totalNonActive} - Non-Active (latest period)` },
  ]
};

// extract the human‐readable periods:
const compareUsersCategories = compareUsersData.map(r => r.period);

// const compareUsersChartOptions = {
//   bar: {
//     ...baseBarOpts,
//     chart: {
//       // copy any existing chart props (type, toolbar)…
//       ...baseBarOpts.chart,
//       height: compareUsersStat.chartHeight
//     },
//     colors: [ themeColor1, tone1, tone2 ]
//   },
//   // you can leave your line options untouched if you never use them here:
//   line: getChartOptions().line
// };
// 1) Define a tooltip that shows both the period label and the count
const usersCompareTooltip = {
  bar: {
    x: {
      show: true,
      formatter: (_val, { dataPointIndex, w }) =>
        w.config.xaxis.categories[dataPointIndex]
    },
    y: {
      formatter: count => `${count.toLocaleString()} users`
    }
  }
};

// get the base options from your helper
const baseOpts = getChartOptions(usersCompareTooltip);

// now override just the bar options
const compareUsersChartOptions = {
  bar: {
    ...baseOpts.bar,
    xaxis: {
      ...baseOpts.bar.xaxis,
      categories: compareUsersCategories
    },
    chart: {
      ...baseOpts.bar.chart,
      height: compareUsersStat.chartHeight
    },
    colors: [ themeColor1, tone1, tone2 ]
  },
  line: baseOpts.line
};

//Automatically refresh Top Cards Functionality
// 2 minutes refresh
const REFRESH_MS = 120000;

// Keep latest filter/limit values in refs so the interval always uses current values
const refreshParamsRef = useRef({
  craftedPeriod,
  craftedLimit,
  hwPeriod,
  hwLimit,
  hwDeviceFilter,
  cuPeriod,
  cuLimit
});

// Update the ref whenever these values change
useEffect(() => {
  refreshParamsRef.current = {
    craftedPeriod,
    craftedLimit,
    hwPeriod,
    hwLimit,
    hwDeviceFilter,
    cuPeriod,
    cuLimit
  };
}, [craftedPeriod, craftedLimit, hwPeriod, hwLimit, hwDeviceFilter, cuPeriod, cuLimit]);

useEffect(() => {
  const refreshTopTiles = () => {
    // TOTAL REGISTERED USERS tile
    dispatch(fetchDashboardStatsQ1(0));
    dispatch(fetchUserTileInfo("0", "barsysdb.users", startTime, endTime, "0", "99", 0));

    // CRAFTED COCKTAILS tile
    dispatch(fetchDashboardStatsQ3(0));

    // HARDWARE DEPLOYED tile
    dispatch(fetchHardwareChartTileInfo(0));

    // USERS COMPARISON tile (uses current period/limit)
    const { cuPeriod: p, cuLimit: l } = refreshParamsRef.current;
    dispatch(fetchCompareUsers(p?.value ?? "month", String(l ?? "12"), 0));
  };

  // fire once immediately
  refreshTopTiles();

  // then every 2 minutes
  const id = setInterval(refreshTopTiles, REFRESH_MS);

  return () => clearInterval(id);
}, [dispatch, startTime, endTime]);
//Automatic Refresh Functionality ends

// -------------- 
// Render the Dashboard 
// // --------------

  return (
    <div>

      {/* DashboardStatCards */}
      <div className="row">
        <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
          <TotalRegisteredUsersCard
            stats_q1={stats_q1}
            loading_q1={loading_q1}
            user_tile_info={user_tile_info}
            loading_user_tile_info={loading_user_tile_info}
            passedDispatch={dispatch}
            themeCol={themeColor1}
            themeFont={themeFont}
            chartOptions={customChartOptions}
          />
        </div>

        <div className="col-lg-3 col-md-6 col-sm-12 mb-3">

          <TopCardWithModal
            stat={cocktailStats}
            loading={loading_q3}
            chartOptions={chartOptionsCrafted}
            modalContent={
              <CraftedCocktailsModalContent
                total={stats_q3?.make}
                loading={loading_crafted_cocktails_expand}
                data={crafted_cocktails_expand_info}
                chartOptions={craftedChartOptions}
                chartSeries={craftedChartData}
                craftedPeriod={craftedPeriod}
                craftedLimitInput={craftedLimitInput}
                setCraftedLimitInput={setCraftedLimitInput}
                setCraftedPeriod={setCraftedPeriod}
                craftedLimit={craftedLimit}
                setCraftedLimit={setCraftedLimit}
                craftedCustomFrom={craftedCustomFrom}
                setCraftedCustomFrom={setCraftedCustomFrom}
                craftedCustomTo={craftedCustomTo}
                setCraftedCustomTo={setCraftedCustomTo}
              />
            }
          />

        </div>

          {/* ─── HARDWARE DEPLOYED ───────────────────────────── */}
          <div className="col-lg-3 col-md-6 mb-3">
            <TopCardWithModal
              stat={{
                title: 'HARDWARE DEPLOYED',
                total: Number(hardware_chart_tile_info?.totalDevices || 0),
                chartType: 'bar',
                chartHeight: 60,
                chartData: [{
                  name: 'Devices',
                  data: [
                    { x: 'Previous Week', y: prev },
                    { x: 'Current Week',  y: curr }
                  ]
                }],
                info: [
                  { icon:'fa fa-chart-line fa-fw', text:`${hardware_chart_tile_info?.previousWeek ?? 0} - Previous Week` },
                  { icon:'fa fa-chart-bar fa-fw',  text:`${hardware_chart_tile_info?.currentWeek ?? 0} - Current Week` },
                  { icon:'fa fa-percent fa-fw',    text:`Change: `,  text2: `${deltaPercent}`, tooltip: 'Change vs. Previous', color: parseInt(deltaPercent) >= 0 ? 'green' : 'red' },
                  { icon: 'fa fa-percent fa-fw',    text: `Current is ${ofTotalPercent} of Total`, tooltip: '% of Total' },      
                ],
              }}
              loading={hardware_chart_tile_info_loading}
              chartOptions={hardwareChartOptions}
              modalContent={
                <HardwareUsageModalContent
                  data={hardware_usage_expand_info}
                  loading={loading_hardware_usage_expand}
                  hwPeriod={hwPeriod}
                  setHwPeriod={setHwPeriod}
                  hwCustomFrom={hwCustomFrom}
                  setHwCustomFrom={setHwCustomFrom}
                  hwCustomTo={hwCustomTo}
                  setHwCustomTo={setHwCustomTo}
                  hwDeviceFilter={hwDeviceFilter}
                  setHwDeviceFilter={setHwDeviceFilter}
                  hwLimitInput={hwLimitInput}
                  setHwLimitInput={setHwLimitInput}
                  themeColor={getThemeColor()}
                />
              }
            />
          </div>


          <div className="col-lg-3 col-md-6 col-sm-12 mb-3">
            <TopCardWithModal
              stat={compareUsersStat}
              loading={cuLoading}
              chartOptions={compareUsersChartOptions}

              modalContent={
                <CompareUsersModalContent
                  data={cuData}
                  loading={cuLoading}
                  period={cuPeriod}
                  setPeriod={setCuPeriod}
                  limitInput={cuLimitInput}
                  setLimitInput={setCuLimitInput}
                  themeColor={themeColor1}
                  chartOptions={{ bar: compareUsersExpandedOptions }}
                />
              }
            />
          </div>

      </div>
      
      {/* New Compare Users Section */}
      <div className="row">
        <div className="col-md-6 mb-4">
          
          {loading_compare_users_day ? (
            <div className="col-md-12 mb-8">
              <CardLoader
                title={defaultNewVsActiveStat.title}
                info={defaultNewVsActiveStat.info}
                chartHeight={defaultNewVsActiveStat.chartHeight}
                style={{ minHeight: 540 }}
                themeColor={themeColor1}
                // Add other props as needed
              />
            </div>
          ) : compare_users_day_error ? (
            <div className="alert alert-danger">{compare_users_day_error}</div>
          ) : (
            <>
              <ReusableChartCard
                title="NEW USERS VS ACTIVE USERS"
                totalValue={totalNewUsers}
                secondaryValue={totalActiveUsers}
                secondaryLabel=""
                chartType="line"
                chartOptions={newVsActiveChartOptions}
                chartSeries={newVsActiveChartSeries}
                footerText="" // or any footer text you might need
                footerIconClass=""
                height="541px"
                tabs={compareUsersTabs}
                activeTab={compareUsersTab}
                onTabChange={(tab) => setCompareUsersTab(tab)}
                limit={compareUsersLimitInput}
                onLimitChange={handleLimitChangeUsers}
              />
            </>
          )}
        </div>
        

        {/* First half for Top Products list */}
       <div className="col-md-6 mb-4">
          {/* <div className="text-center mb-2">
            <h2>
              Top{" "}
              {topRecipesActiveTab
                ? topRecipesActiveTab.charAt(0).toUpperCase() +
                  topRecipesActiveTab.slice(1)
                : "Recipes"}
            </h2>
          </div> */}
          {(/* error handling if any */ false) && (
            <div className="alert alert-danger">
              {/* Display appropriate error */}
            </div>
          )}

          <ImageCountViewsList
            data={
              topRecipesActiveTab === "ingredients"
                ? ingredients_v3_info || []
                : topRecipesActiveTab === "brands"
                ? brands_v3 || []
                : topRecipesActiveTab === "influencers"
                ? influencerV3Data || []
                : curated_recipes_v3_info || []
            }
            tabs={producttabs}
            activeTab={topRecipesActiveTab}
            onTabChange={(tab) => setTopRecipesActiveTab(tab)}
            onLimitChange={(value) => setTopRecipesLimit(value)}
            loading={
              topRecipesActiveTab === "ingredients"
                ? loading_ingredients_v3_info
                : topRecipesActiveTab === "brands"
                ? loading_brands_v3
                : topRecipesActiveTab === "influencers"
                ? loading_influencer_v3
                : loading_curated_recipes_v3_info
            }
            height={'416px'}
            limit={topRecipesLimit}
            title={'TOP PRODUCTS'}
          />
        </div>

      </div>


    <div className="row align-items-start">
     
      {/* Second half for User Recipes list */}
      <div className="col-md-6 mb-4">
        <UserRecipesList
          recipes={recipesData}
          loading={recipesLoading}
          recipesLimit={userRecipesLimit}   // Shared limit for all tabs
          tabs={[
            { id: "users", label: "Users" },
            { id: "brand", label: "Brand" },
            { id: "curated", label: "Curated Recipes" },
            { id: "all", label: "All Recipes" }, 
          ]}
          activeTab={userRecipesActiveTab}
          onTabChange={(tab) => setUserRecipesActiveTab(tab)}
          onLimitChange={(value) => handleUserRecipesLimitChange(value)}
        />
      </div>
      
      <div className="col-md-6 mb-4">
        <ImageCountViewsList
          // The same data is passed as before – now it is expected that this
          // component will display counts like shareCount, likeCount, etc.
          data={mixlistsData}
          // The tabs remain the same: showing Mixlists, Brands, and Influencers.
          tabs={[
            { id: "mixlists", label: "Mixlists" },
            { id: "brands", label: "Brands" },
            { id: "influencers", label: "Influencers" },
          ]}
          activeTab={mixlistsActiveTab}
          // The handlers update the active tab and the shared limit.
          onTabChange={(tab) => setMixlistsActiveTab(tab)}
          onLimitChange={(value) => setMixlistsLimit(value)}
          loading={mixlistsLoading}
          limit={mixlistsLimit}
          title={'TOP MIXLISTS'}
          height={'400px'}
        />
      </div>

    </div>
    <div>
    {/* Orders table using the orders_info from Redux */}
      {/* <div> */}
        {/* Conditionally render the spinner or the OrdersTable */}
        {/* {loading_orders_info ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <Spinner animation="border" variant="primary">
              <span className="visually-hidden">Loading orders...</span>
            </Spinner>
        </div>
        ) : (
          <OrdersTable
            orders={orders_info}
            orderdetails={order_details_info}
            loading_order_details={loading_order_details}
            title="Orders"
            activePeriod={orderActivePeriod} // controlled in Dashboard state, for example.
            onPeriodChange={(period) => {
              // Save the new period in dashboard state...
              setOrderActivePeriod(period);
              // Compute the date range externally:
              const { dateFrom, dateTo } = computeOrderDateRange(period);
              // Then dispatch:
              dispatch(fetchOrders(dateFrom, dateTo));
            }}
          />
        )}
      </div> */}
      {/* …inside your Dashboard render()… */}
        <div className="row">
          <div className="col-md-6 mb-4">
            <Card>
              <CardBody>
                {/* Header / Limit Row */}
                {/* Title (left aligned) */}
                <div className="mb-2">
                  <div className='text-inverse text-opacity-75 fw-bold fs-16px mb-2' style={{ fontFamily: themeFont }}>LAST CONNECTED DEVICES</div>
                </div>

                {/* Limit input (centered) */}
                <div className="mb-3 d-flex justify-content-center">
                  <div style={{ width: "100px" }}>
                    <input
                      type="text"
                      className="form-control form-control-sm text-center"
                      placeholder="Limit"
                      value={hardwareLimitInput}
                      onChange={handleHardwareLimitChange}
                      style={{
                        color: themeColor1,
                        backgroundColor: "transparent",
                        borderColor: themeColor1,
                        borderRadius: "0.5rem",
                        padding: "0.25rem 0.5rem",
                        fontSize: "0.9rem",
                      }}
                    />
                  </div>
                </div>



                  {/* “Showing 1 – endIndexHardware of totalDevicesFromRedux” */}
                  <div
                    className="mb-2"
                    style={{ color: "#fff", fontSize: "14px", textAlign: "right" }}
                  >
                      {dashboardDevicesLoading ? (
                          <small>Loading devices...</small>
                        ) : ( null
                      )}
                  </div>

                  {/* Table area (half width) */}
                  <div
                    className="table-responsive"
                    style={{
                      minHeight: "200px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: dashboardDevices.length ? "flex-start" : "center"
                    }}
                  >
                      {dashboardDevicesLoading ? (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "200px", // match minHeight
                          }}
                        >
                          <div
                            className="spinner-border"
                            role="status"
                            style={{ width: "3rem", height: "3rem", color: themeColor1 }}
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        </div>
                      ) : dashboardDevices.length === 0 ? (
                        <div style={{ color: "#888" }}>No devices found.</div>
                      ) : (
                        <table className="table table-bordered w-100">
                          <tbody>
                              {dashboardDevices
                                .slice(0, Number(hardwareLimit))
                                .map((device, idx) => (
                                  <DeviceRow
                                    key={device.device_name || idx}
                                    device={device}
                                    rank={idx + 1}
                                    themeColor={themeColor1}
                                    onView={(d) =>
                                      navigate(`/hardware-profile/${encodeURIComponent(d.device_name)}`)
                                    }
                                  />
                                ))}
                          </tbody>
                        </table>
                      )}
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-md-6 mb-4">
              <Card>
                <CardBody>
                  <div className="mb-2">
                    <div className="text-inverse text-opacity-75 fw-bold fs-16px mb-2">
                      LAST CONNECTED USERS
                    </div>
                  </div>

                  {/* Users “Limit” input (centered) */}
                  <div className="mb-3 d-flex justify-content-center">
                    <div style={{ width: "100px" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm text-center"
                        placeholder="Limit"
                        value={userLimitInput}
                        onChange={(e) => setUserLimitInput(e.target.value)}
                        style={{
                          color: themeColor1,
                          backgroundColor: "transparent",
                          borderColor: themeColor1,
                          borderRadius: "0.5rem",
                          padding: "0.25rem 0.5rem",
                          fontSize: "0.9rem",
                        }}
                      />
                    </div>
                  </div>

                  <div
                    className="table-responsive"
                    style={{
                      minHeight: "200px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: paginatedUserList.length ? "flex-start" : "center",
                    }}
                  >
                    {usersLoadingPaginated ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: "200px", // match minHeight
                        }}
                      >
                        <div
                          className="spinner-border"
                          role="status"
                          style={{ width: "3rem", height: "3rem", color: themeColor1 }}
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    ) : paginatedUserList.length === 0 ? (
                      <div style={{ color: "#888" }}>No users found.</div>
                    ) : (
                      <table className="table table-bordered w-100">
                        <tbody>
                          {paginatedUserList
                            .slice(0, Number(userLimit))
                            .map((user, idx) => (
                              <DashUserRow
                                key={user.id || user.userId || user._id || idx}
                                user={user}
                                rank={idx + 1}
                                themeColor={themeColor1}
                              />
                            ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>

    </div>
      {/* BigQuery-only insights, added below the unchanged MySQL cards */}
      <BqDashboardInsights />
      {/* <div id="world-map" className="mb-4"></div> */}
    </div>
  );
}

export default Dashboard;